import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, TextField, Typography, List, Chip, ListItem, ListItemText, InputAdornment, CircularProgress, Paper } from '@material-ui/core'
import compose from 'recompose/compose'
import * as S from './styled'
import { withI18n } from '../../hocs'
import { Search } from 'lucide-react'
import _, { debounce } from 'lodash'
import { assignProcess } from '../../../api/index'
import { AlertDialog } from '../index'
import ActionDialog from '../action-dialog'
import { useMutation } from '@tanstack/react-query'
import i18n from '../../../assets/i18n'
import { Autocomplete } from '@material-ui/lab'
import { useVacancyRequestToAssignCandidates } from '../../../api/queries/useVacancyRequestManager'
import { useAppContext } from '../../../context/appContext'
import { ALERT_LEVEL } from '../../../config/constants'
import moment from 'moment'
import { tokens } from '../../../assets/tokens'

const AddCandidateButton = ({ ids, disabled }) => {
  const { createAlert } = useAppContext()

  const [open, setOpen] = useState(false)
  const [areUSure, setAreUSure] = useState(false)
  const [dataProcess, setDataProcess] = useState([])
  const [hoveredItem, setHoveredItem] = useState(null)
  const [autocompleteKey, setAutocompleteKey] = useState(0)
  const [formData, setFormData] = React.useState({ process: {} })
  const [selectedProcessId, setSelectedProcessId] = useState(null)
  const [autocompleteInputValue, setAutocompleteInputValue] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const justSelectedRef = useRef(false)

  const { data, isFetching: isVacancyRequestsRefetching } = useVacancyRequestToAssignCandidates(searchTerm || null, {
    enabled: open
  })

  const { data: dataLatest } = useVacancyRequestToAssignCandidates(null, {
    enabled: open
  })

  const debouncedSetSearchTerm = useMemo(() => debounce(setSearchTerm, 500), [])

  useEffect(() => {
    if (open) {
      if (justSelectedRef.current) {
        justSelectedRef.current = false
        return
      }
      debouncedSetSearchTerm(autocompleteInputValue)
    }
    return () => debouncedSetSearchTerm.cancel()
  }, [autocompleteInputValue, open, debouncedSetSearchTerm])

  const addProcess = async data => {
    try {
      await assignProcess(data)
    } catch (error) {
      console.error('Error while creating process:', error)
      throw error
    }
  }

  const mutationAssignProcess = useMutation(addProcess)

  // Sin evento cuando llega desde el botón Asignar del ActionDialog, que vive fuera del form
  const handleSubmit = e => {
    e && e.preventDefault && e.preventDefault()

    if (!isValid) {
      return
    }

    setAreUSure(true)
  }

  const isValid = () => {
    return !_.isEmpty(formData.process)
  }

  // Devuelve la promesa: spinner en Aceptar y los modales se cierran cuando han terminado todas las asignaciones
  const handleConfirm = () => {
    return Promise.all(
      ids.map(id => {
        const data = {
          candidature_id: id.id,
          vacancy_id: formData.process.id
        }
        return mutationAssignProcess.mutateAsync(data)
      })
    )
      .then(() => {
        setAreUSure(false)
        setOpen(false)
        createAlert(i18n.phases_states.assignedSuccessfully, ALERT_LEVEL.SUCCESS)
      })
      .catch(error => {
        console.log(error)
        createAlert(error?.msg?.message ?? error?.message, ALERT_LEVEL.ERROR)
      })
  }

  const handleChangeProcess = (event, newValue) => {
    justSelectedRef.current = true
    setFormData({ ...formData, process: newValue ?? {} })
    setSelectedProcessId(null)
  }

  const handleOnInputChange = (event, newValue, reason) => {
    if (reason === 'clear') {
      setAutocompleteInputValue('')
      return;
    }
    setAutocompleteInputValue(newValue)
  }

  const colors = {
    active: '#D1E5FC',
    closed: '#D8DDE3',
    rejected: '#FF6961',
    empty: '#A699FA',
    pending: '#FFCAA2',
    cancelled: '#FFA2A2'
  }

  const mapItem = item => {
    let displayName = item?.search_name ?? item?.candidature_settings?.name + ' - ' + item?.client_place?.name + ' - ' + moment(item?.created_at).format('DD/MM/YYYY')
    if (item?.search_name) {
      const parts = item.search_name.split(' | ')
      if (parts.length >= 4 && parts[3]) {
        const workingDayKey = parts[3].trim()
        if (i18n.templateRequestManager.workingDay[workingDayKey]) {
          parts[3] = i18n.templateRequestManager.workingDay[workingDayKey]
          displayName = parts.join(' | ')
        }
      }
    }
    displayName = String(item?.id).substring(0, 8) + ' - ' + displayName
    return { id: item.id, name: displayName, state: item?.state, color: colors[item?.state] || colors.closed }
  }

  useEffect(() => {
    const dataFiltered = (data?.data ?? []).map(mapItem)

    const idFiltered = searchTerm
      ? (dataLatest?.data ?? [])
        .filter(item => String(item.id).toLowerCase().includes(searchTerm.toLowerCase()))
        .map(mapItem)
      : []

    const seenIds = new Set(dataFiltered.map(i => i.id))
    const merged = [...dataFiltered, ...idFiltered.filter(i => !seenIds.has(i.id))]

    setDataProcess(merged)
  }, [data, dataLatest, searchTerm])

  const [dataLatestOptions, setDataLatestOptions] = useState([])

  useEffect(() => {
    const latest = dataLatest?.data
      ?.slice(0, 5)
      ?.map(mapItem)
    setDataLatestOptions(latest ?? [])
  }, [dataLatest])

  return (
    <>
      <ActionDialog
        open={open}
        title={i18n.assignedModal.title}
        onClose={() => setOpen(false)}
        onConfirm={handleSubmit}
        cancelLabel={i18n.dialog.decline}
        confirmLabel={i18n.assignedModal.assign}
        confirmDisabled={!isValid()}
        disableBackdropClose
        maxWidth="sm"
      >
        <form onSubmit={handleSubmit}>
          <div className="group-input">
              <Autocomplete
                key={autocompleteKey}
                size="small"
                className="autocomplete-custom"
                value={_.isEmpty(formData.process) ? null : formData.process}
                inputValue={autocompleteInputValue}
                options={dataProcess}
                filterOptions={x => x}
                getOptionLabel={option => (option && option.name) ? option.name : ''}
                getOptionSelected={(option, value) => option?.id === value?.id}
                PaperComponent={props => (
                  <Paper {...props} elevation={8} style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.18)', borderRadius: 8 }} />
                )}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={i18n.assignedModal.placeholder}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          {isVacancyRequestsRefetching && searchTerm
                            ? <CircularProgress size={20} />
                            : <Search size={18} color={tokens.color.text.tertiary} />}
                        </InputAdornment>
                      )
                    }}
                  />
                )}
                renderOption={option => (
                  <>
                    <ListItemText primary={option.name} />
                    <Chip
                      label={i18n.templateRequestManager.vacancyStates[option.state]}
                      size="small"
                      style={{
                        backgroundColor: option.color,
                        marginLeft: 'auto'
                      }}
                    />
                  </>
                )}
                loading={isVacancyRequestsRefetching}
                loadingText={i18n.assignedModal.loading}
                onChange={(event, newValue) => handleChangeProcess(event, newValue)}
                onInputChange={handleOnInputChange}
              />

              <Box pt={2} display='flex' flexDirection='row' gridGap={8}>
                <Typography variant="subtitle1">
                  <strong>{i18n.assignedModal.latest}</strong>
                </Typography>
                {isVacancyRequestsRefetching && (
                  <CircularProgress size={20} />
                )}
              </Box>
              <List>
                {dataLatestOptions.map(process => (
                  <ListItem
                    key={process?.id}
                    disablePadding
                    style={{
                      cursor: 'pointer',
                      backgroundColor: selectedProcessId === process?.id ? tokens.color.background.light : hoveredItem === process?.id ? tokens.color.background.surface : 'inherit'
                    }}
                    onMouseEnter={() => setHoveredItem(process?.id)}
                    onMouseLeave={() => setHoveredItem(null)}
                    onClick={() => {
                      justSelectedRef.current = true
                      setAutocompleteKey(prevKey => prevKey + 1)
                      setFormData({
                        ...formData,
                        process: {
                          id: process?.id,
                          name: process?.name,
                          state: process?.state
                        }
                      })
                      setAutocompleteInputValue(process?.name ?? '')
                      setSelectedProcessId(process?.id)
                    }}
                  >
                    <ListItemText primary={process?.name} />
                    <Chip
                      label={i18n.templateRequestManager.vacancyStates[process?.state]}
                      size="small"
                      style={{
                        backgroundColor: colors[process?.state] || colors.closed
                      }}
                    />
                  </ListItem>
                ))}
              </List>
          </div>

          <AlertDialog
            open={areUSure}
            onClose={() => setAreUSure(false)}
            onSuccess={e => handleConfirm(e)}
            title={i18n.assignedModal.subModalTitle}
            size="sm"
            text={
              <>
                <Typography variant="body1">{i18n.assignedModal.subModalSubTitle}</Typography>
                <Box mt={2}>
                  <ul>
                    {ids.map(id => (
                      <li key={id}>
                        <strong>{id.name}</strong>
                      </li>
                    ))}
                  </ul>
                </Box>

                <Typography variant="body1">
                  {i18n.assignedModal.subModalSubDescription} <strong>{formData.process.name}</strong>
                </Typography>
              </>
            }
          />
        </form>
      </ActionDialog>

      <S.ButtonExport variant="contained" color="secondary" onClick={() => setOpen(true)} disabled={disabled}>
        {i18n.processesTable.assignProcess} ({ids && ids.length > 0 ? ids.length : 0})
      </S.ButtonExport>
    </>
  )
}

const enharce = compose(withI18n)

export default enharce(AddCandidateButton)
